import React from 'react';
import DocumentPage, {
  Section,
  SubSection,
  Paragraph,
  BulletList,
  DoDoNot,
} from './DocumentPage';

const LaddersStepladdersDoc = ({ onReturnHome }) => (
  <DocumentPage
    pageTitle="Ladders and Stepladders – Safe System of Work"
    documentHeading="Safe System of Work: Ladders and Stepladders"
    meta={[
      ['Document Reference', 'PVS-SSOW-LAD'],
      ['Version', '1.0'],
      ['Date of Issue', '17th March 2026'],
      ['Next Review Date', '17th March 2028'],
      ['Activity', 'Safe use of ladders, stepladders, telescopic and combination / multi-purpose ladders'],
      ['Review Period', '24 months'],
    ]}
    acknowledgment={{
      documentName: 'Ladders and Stepladders – Safe System of Work',
      documentReference: 'PVS-SSOW-LAD',
      processActivity: 'Safe Use of Ladders and Stepladders',
      versionNumber: '1.0',
      dateOfIssue: '17th March 2026',
    }}
    onReturnHome={onReturnHome}
  >
    <Section number="1" title="Causes of Falls from Ladders and Stepladders">
      <Paragraph>
        Falls from height are the most common cause of death and serious injury to people at work.
        When working with ladders and step ladders death or injury can occur when over-reaching,
        when the equipment is not placed on firm / level ground, when equipment does not meet
        required British Standards, when the equipment is not properly secured, when the equipment
        is struck by a moving vehicle or when the equipment fails due to poor maintenance.
      </Paragraph>
    </Section>

    <Section number="2" title="When is a Ladder or Stepladder the Most Suitable Equipment?">
      <Paragraph>
        The law says that ladders and step-ladders can be used when a risk assessment has shown that
        using other equipment is not justified because of the low risk and short duration of use;
        or there are existing workplace features which cannot be altered. As a general guide, if
        your task requires the use of a leaning ladder or stepladder for more than 30 minutes at a
        time then alternative equipment should be considered.
      </Paragraph>
      <Paragraph className="mt-3">
        Only use ladders and stepladders in situations where they can be used safely, for example:
      </Paragraph>
      <BulletList
        items={[
          'On firm level ground.',
          'On clean, solid surfaces. These need to be clean (no oil or other spillages) and free of loose material so the feet can grip.',
          'Where they will not be pushed over by other hazards such as doors or windows.',
          'Near windows and doors when they have been secured shut (not fire exits).',
          'Where they will not be struck by vehicles (protect the area using suitable barriers or cones).',
          'Where the general public are prevented from using it, walking underneath it or being at risk because they are too near (use barriers, cones or, as a last resort, a person standing guard at the base).',
        ]}
      />
      <Paragraph className="mt-4 font-semibold">
        NEVER climb on racking or shelving or stand on chairs, always use the correct equipment for
        the task. If you are working on ladders and stepladders always wear suitable footwear and
        NEVER throw things down onto the floor below.
      </Paragraph>
    </Section>

    <Section number="3" title="Checking the Condition of Ladders and Stepladders (Pre-Use Checks)">
      <Paragraph className="mb-3">
        The purpose of a detailed visual inspection or pre-use check is to spot defects. When doing
        an inspection or pre-use check, you should:
      </Paragraph>
      <BulletList
        items={[
          'Check the stiles – make sure they are not bent, twisted or dented as the ladder could buckle or collapse.',
          'Check the feet – if feet are missing, worn or damaged the ladder could slip. Also check ladder feet when moving from soft or dirty ground to a smooth, solid surface, to make sure the foot material and not the dirt is making contact with the ground.',
          'Check the rungs – if they are cracked, bent, worn, missing or loose the ladder could fail.',
          'Check any locking mechanisms – if they are bent or the fixings are worn or damaged the ladder could collapse. Ensure any locking bars are engaged.',
          'Check the stepladder platform – if it is split or buckled the ladder could become unstable or collapse.',
          'Check the steps or treads on stepladders – if they are contaminated they could be slippery; if the fixings are loose, they could collapse.',
          'Check the rivets or stays – if they are cracked, loose or damaged the ladder could fail.',
          'Check for missing or damaged tie rods.',
          "Check the ladder stability devices and other accessories in accordance with the manufacturer's instructions.",
          'Check the ladder meets required British Standards. The HSE recommends British Standard Class 1 "Industrial" or BS EN 131 (also known as "professional") ladders for use at work. Do not use domestic type ladders or step ladders in the work environment.',
          'Check weight restrictions on equipment. All ladders and stepladders have weight limits applied, especially telescopic ladders.',
        ]}
      />
      <Paragraph className="mt-4">
        If you spot any of the above defects, do not use the equipment and notify your Supervisor or
        Manager immediately.
      </Paragraph>
    </Section>

    <Section number="4" title="Safe Use of Leaning Ladders">
      <DoDoNot
        doItems={[
          'Only carry light materials and tools and ensure you assess the risks.',
          'Make sure the ladder is long enough or high enough for the task.',
          'Make sure the ladder angle is at 75° – you should use the 1 in 4 rule (i.e. 1 unit out for every 4 units up).',
          "Always grip the ladder and face the ladder rungs while climbing or descending – don't slide down the stiles.",
          'Avoid holding items when climbing (consider using a tool belt or lanyards).',
          'Maintain three points of contact when climbing (e.g. one hand and two feet) and wherever possible at the work position.',
          'Where you cannot maintain a handhold, other than for a brief period (e.g. starting a screw), take other measures to prevent a fall or reduce the consequences.',
          'Secure the ladder (e.g. by tying it to prevent it slipping outwards or sideways) and have a strong upper resting point – do not rest a ladder against weak upper surfaces (e.g. glazing or plastic gutters).',
          'Use an effective stability device if necessary.',
          'Use a non-conductive ladder (e.g. fibreglass) for any electrical work.',
          'Carry out pre-use checks and ensure they are thorough, checking the components and operation of each and every locking mechanism. Especially relevant to telescopic and combination ladders.',
          'Cordon off the area underneath the area being worked on to prevent injuries / damage from falling objects.',
        ]}
        doNotItems={[
          'Overreach – make sure your belt buckle (navel) stays within the stiles.',
          'Overload it – consider your own weight and the equipment or materials you are carrying before working at height. Check the pictogram or label on the ladder.',
          "Don't try to move or extend ladders while standing on the rungs.",
          'Work off the top three rungs; try to make sure the ladder extends at least 1m (three rungs) above where you are working.',
          'Stand ladders on moveable objects, such as pallets, lift trucks, tower scaffolds, vans, or mobile elevating work platforms.',
          'Jump from a ladder.',
          'Have more than one person on the ladder at any one time.',
          'Work within 6m horizontally of any overhead power line, unless it has been made dead or protected with insulation.',
        ]}
      />
    </Section>

    <Section number="5" title="Safe Use of Telescopic Ladders">
      <Paragraph className="mb-3">
        These are a variety of leaning ladder; while they save space, they can be more prone to
        twisting and bending. There are a number of types of telescopic ladder and they don't all
        work the same way – understand and follow the manufacturer's instructions.
      </Paragraph>
      <DoDoNot
        doItems={[
          'Before use check the ladder is operating correctly and locking mechanisms are working.',
          'Check all sections are locked before using the ladder.',
          'Follow user instructions for opening and closing the ladder.',
          'Take care to avoid trapping fingers when closing a ladder.',
          'Ensure telescopic ladders are used, stored and transported carefully and kept clean.',
        ]}
        doNotItems={['Use a telescopic ladder if you are in any doubt about the ladder or locking mechanisms.']}
      />
    </Section>

    <Section number="6" title="Safe Use of Stepladders">
      <DoDoNot
        doItems={[
          'Check all four stepladder feet are in contact with the ground and the steps are level.',
          'Only carry light materials and tools.',
          'Ensure any locking devices are engaged.',
          'Try to position the stepladder to face the work activity and not side on. However, a risk assessment may show it is safer to work side on, e.g. in narrow aisles where you can fully lock the stepladder to work side on.',
          'Try to avoid work that imposes a side loading, such as side-on drilling through solid materials.',
          'Where side-on loadings cannot be avoided, prevent the steps from tipping over, e.g. by tying the steps. Otherwise use a more suitable type of access equipment.',
          'Maintain three points of contact at the working position – two feet and one hand, or when both hands need to be free briefly, two feet and the body supported by the stepladder.',
        ]}
        doNotItems={[
          'Overreach.',
          'Stand and work on the top three steps (including a step forming the very top of the stepladder) unless there is a suitable handhold.',
        ]}
      />
      <SubSection title="Working Without a Handhold – Risk-Based Considerations">
        <BulletList
          items={[
            'The height of the task.',
            'Whether a handhold is still available to steady yourself before and after the task.',
            'Whether it is light work.',
            'Whether it avoids side loading.',
            'Whether it avoids overreaching.',
            'Whether the stepladder can be tied (for example when working side-on).',
          ]}
        />
      </SubSection>
    </Section>

    <Section number="7" title="Safe Use of Combination / Multi-Purpose Ladders">
      <Paragraph className="mb-3">
        These are a variation of ladders and stepladders that due to their mechanisms can be used in
        different configurations (sometimes known as A-frame ladders). Understand and follow the
        manufacturer's instructions.
      </Paragraph>
      <DoDoNot
        doItems={[
          'Check that locking mechanisms are engaged before use.',
          'Recheck locking mechanisms whenever the setup of the ladder is changed.',
          'Always follow instructions in the user manual when using the ladder in different configurations.',
        ]}
        doNotItems={[
          'Use a multi-purpose ladder without engaging the locking mechanisms.',
          'Extend the top section of a ladder beyond limits set out in the user manual or marked on the ladder.',
        ]}
      />
    </Section>

    <Section number="8" title="Securing Ladders">
      <BulletList
        items={[
          'Tie the ladder to a suitable point, making sure both stiles are tied.',
          'Where this is not practical, secure with an effective ladder stability device.',
          'If this is not possible, then securely wedge the ladder, for example wedging the stiles against a wall.',
          "If none of these options can be achieved, foot the ladder. Footing is always the last resort. Avoid it, where 'reasonably practicable', by using other access equipment.",
        ]}
      />
    </Section>

    <Section number="9" title="Ladders for Access Purposes">
      <BulletList
        items={[
          'Ladders used to access another level should be tied and extend at least 1m above the landing point to provide a secure handhold.',
          'At ladder access points, a self-closing gate is recommended.',
          'Stepladders should not be used to access another level, unless they have been specifically designed for this purpose.',
        ]}
      />
    </Section>

    <Section number="10" title="Inspection & Maintenance of Ladders">
      <BulletList
        items={[
          'The ladders held by the company are recorded on a register and are inspected on a systematic basis by a competent person.',
          'The ladders are stored well, transported carefully and maintained.',
          'This must include cleaning as dirt and grit etc. can affect locking mechanisms, especially of stepladders, combination and telescopic ladders.',
        ]}
      />
    </Section>

    <Section number="11" title="Reminder">
      <Paragraph className="font-semibold">
        If you are not sure about how to work safely when using ladders or stepladders, please speak
        to your Supervisor or Manager. You have a duty not only to protect your own health and
        safety but also to ensure that your actions do not put others around you at risk.
      </Paragraph>
    </Section>
  </DocumentPage>
);

export default LaddersStepladdersDoc;
